import { useState } from "react";
import { Link } from "react-router-dom";
import type { CustomerOrder } from "../../api/types";
import { Modal } from "../Modal";
import { useAuth } from "../../state/AuthContext";
import { useT } from "../../i18n/LanguageProvider";
import { localDayKey } from "../../utils/dayKey";
import { OrderActions } from "./OrderActions";
import { OrderDetailModal } from "./OrderDetailModal";
import {
  deliveryJobStatusClass,
  deliveryJobStatusLabel,
  fulfillmentLabel,
  orderLinesSummary,
  orderSlotLabel,
  orderStatusClass,
  orderStatusLabel,
} from "./orderUi";

export function DayOrdersModal({
  date,
  orders,
  onClose,
  onChanged,
}: {
  date: Date;
  orders: CustomerOrder[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { hasRole } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const title = date.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  // Owner report 2026-07-29: the day view was a dead end — it knew the date
  // but offered no way to create an order for it. Deep-link the builder with
  // the day prefilled (route is OWNER/MANAGER-gated, so mirror that here).
  // Past days never offer creation — orders exist only for today onward.
  const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const todayKey = localDayKey(new Date());
  const canCreate = hasRole("OWNER", "MANAGER") && dayKey >= todayKey;

  return (
    <Modal title={`Orders · ${title}`} onClose={onClose}>
      {error ? <div className="alert error">{error}</div> : null}
      {canCreate ? (
        <div className="button-row day-orders-create-row">
          <Link to={`/app/orders/build?day=${dayKey}`} className="button">
            New order for this day
          </Link>
        </div>
      ) : null}
      {orders.length === 0 ? (
        <p className="muted">No orders scheduled for this day.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {orders.map((order) => (
            <DayOrderRow
              key={order.id}
              order={order}
              onChanged={onChanged}
              onError={setError}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function DayOrderRow({
  order,
  onChanged,
  onError,
}: {
  order: CustomerOrder;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const { t } = useT();
  const [busy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // G-16: the canonical per-order detail surface, reachable from every row.
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <div className="day-shifts-row">
      <div className="day-shifts-row-head">
        <div>
          <strong>{orderSlotLabel(t, order)}</strong> · {order.customerName}{" "}
          <span className="muted">· {order.code}</span>
        </div>
        <div style={{ display: "flex", gap: "var(--space-1)", alignItems: "center" }}>
          {order.deliveryJob ? (
            <span className={deliveryJobStatusClass(order.deliveryJob.status)}>
              {deliveryJobStatusLabel(t, order.deliveryJob.status)}
            </span>
          ) : null}
          {/* OD-32: what happened diverged from what was ordered. */}
          {order.divergedFromRequest ? (
            <span className="badge warning">differs from request</span>
          ) : null}
          {/* G-177 (KI-89): the courier delivered, the sale could not be written. */}
          {order.deliveredNotCompleted ? (
            <span className="badge danger">{t("orders.deliveredNotCompleted")}</span>
          ) : null}
          <span className={orderStatusClass(order.status)}>
            {orderStatusLabel(t, order.status)}
          </span>
        </div>
      </div>
      <p style={{ margin: 0 }}>{orderLinesSummary(order)}</p>
      <p className="muted" style={{ margin: 0 }}>
        {fulfillmentLabel(t, order.fulfillment)}
        {order.totalEur ? ` · ${order.totalEur} €` : ""}
        {order.prepaidNotRecorded ? ` · ${t("orders.prepaidNotRecorded")}` : ""}
        {order.customerPhone ? ` · ${order.customerPhone}` : ""}
        {order.source !== "MANUAL" ? ` · ${order.source.toLowerCase()}` : ""}
      </p>
      {expanded ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          {order.customerNotes ? <p style={{ margin: 0 }}>{order.customerNotes}</p> : null}
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {order.lines.map((line) => (
              <li key={line.id}>
                {Number(line.quantity)}× {line.description}
                {line.unitPriceEur ? ` — ${line.unitPriceEur} €` : ""}
              </li>
            ))}
          </ul>
          {order.cancelReason ? (
            <p className="muted" style={{ margin: 0 }}>
              Cancelled: {order.cancelReason}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="button-row">
        <button
          type="button"
          className="button plain"
          onClick={() => setExpanded((cur) => !cur)}
          disabled={busy}
        >
          {expanded ? "Less" : "Details"}
        </button>
        <button
          type="button"
          className="button tinted"
          onClick={() => setDetailOpen(true)}
        >
          Full detail
        </button>
      </div>
      <OrderActions order={order} onChanged={onChanged} onError={onError} />

      {detailOpen ? (
        <OrderDetailModal
          order={order}
          onClose={() => setDetailOpen(false)}
          onChanged={onChanged}
        />
      ) : null}
    </div>
  );
}
